import { formatNumber } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  EventCategories,
  GoogleAnalyticsService,
} from '../../../google/services/google-analytics.service';
import {
  ILatLng,
  Map,
  MapsService,
  Marker,
} from '../../../google/services/maps.service';
import { LoadingGraphic } from '../../controllers/loading-graphic.controller';
import { SideDialogRef } from '../../side-dialog/side-dialog-ref.class';
import { LocationUpdate } from './models/location-update.model';

@Component({
  selector: 'app-elevation-dialog',
  templateUrl: './elevation-dialog.component.html',
  styleUrls: ['./elevation-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ElevationDialogComponent implements AfterViewInit, OnDestroy {
  @ViewChild('searchBox', { static: false }) private searchBox: ElementRef;
  @ViewChild('map', { static: false }) private mapElement: ElementRef;
  private elevationDiv?: HTMLDivElement;

  elevation: number;

  private map: Map;
  private mapPin: Marker;

  public addressSearch: FormControl<string>;

  private _subscriptions: Subscription[] = [];

  constructor(
    private dialogRef: SideDialogRef<LocationUpdate>,
    private loadingGraphic: LoadingGraphic,
    private gaServices: GoogleAnalyticsService,
    private mapsService: MapsService,
    private zone: NgZone
  ) {
    this.elevation = 0;

    this.addressSearch = new FormControl<string>('', [Validators.required]);
  }

  ngAfterViewInit(): void {
    this.gaServices.trackEvent('Elevation Dialog', {
      action: 'Open',
    });

    this.renderMap();

    setTimeout(() => {
      this.addressSearch.markAsUntouched();
      this.searchBox?.nativeElement.focus();
    });
  }

  ngOnDestroy() {
    this._subscriptions.forEach((s) => s.unsubscribe());
  }

  protected searchClick(event?: KeyboardEvent | MouseEvent) {
    if (event) {
      event.cancelBubble = true;
      event.stopPropagation();
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    if (this.addressSearch.hasError('invalidAddress')) {
      this.addressSearch.setErrors(null);
    } else if (this.addressSearch.invalid) {
      return;
    }

    let searching = true;

    this.loadingGraphic.show('Locating...', function () {
      return searching;
    });

    this.gaServices.trackEvent('Elevation Dialog', {
      action: 'Address Search',
    });

    this.gaServices.trackEvent_Old(
      EventCategories.elevationDialog,
      'Searched Address'
    );

    this.mapsService
      .geocodeAddress(this.addressSearch.value)
      .then((results) => {
        if (!results) {
          this.addressSearch.setErrors({ invalidAddress: true });
          this.addressSearch.markAsTouched();
          this.elevation = 0;
          this.elevationDiv!.innerHTML = 'Elevation: 0 feet';

          if (this.mapPin) {
            this.mapPin.remove();
            this.mapPin = undefined;
          }

          this.map.resetView();

          searching = false;

          this.loadingGraphic.hide();

          return;
        }

        const { formattedAddress, ...location } = results;

        this.mapsService.getElevation(location).then((elevation) => {
          this.addressSearch.setValue(formattedAddress);
          this.elevation = elevation;

          this.updatePinLocation(location, false, true);

          searching = false;

          this.loadingGraphic.hide();
        });
      });
  }

  protected currentLocationClick() {
    if (navigator.geolocation) {
      const self = this;

      this.gaServices.trackEvent('Elevation Dialog', {
        action: 'Current Location',
      });

      this.gaServices.trackEvent_Old(
        EventCategories.elevationDialog,
        'Current Location Search'
      );

      navigator.geolocation.getCurrentPosition(function (loc) {
        self.zone.run(() => {
          self.updatePinLocation(
            {
              lat: loc.coords.latitude,
              lng: loc.coords.longitude,
            },
            true,
            true
          );
        });
      });
    }
  }

  protected cancelClick() {
    this.gaServices.trackEvent('Elevation Dialog', {
      action: 'Cancel',
    });

    this.dialogRef.close(null);
  }

  protected okClick() {
    this.gaServices.trackEvent('Elevation Dialog', {
      action: 'OK',
    });

    this.gaServices.trackEvent_Old(
      EventCategories.elevationDialog,
      'Used Elevation'
    );

    this.dialogRef.close(
      new LocationUpdate(this.addressSearch.value, this.elevation)
    );
  }

  protected getErrorText(): string {
    if (this.addressSearch.hasError('required')) {
      return 'Address is required';
    }

    if (this.addressSearch.hasError('invalidAddress')) {
      return 'Invalid address';
    }

    return '';
  }

  private async renderMap() {
    this.map = await this.mapsService.renderMap({
      element: this.mapElement.nativeElement as HTMLDivElement,
      center: {
        lat: 40.66765835357266,
        lng: -97.93289476055907,
      },
      zoom: 4,
      streetViewControl: false,
      fullscreenControl: false,
      mapTypeControl: false,
      zoomControl: true,
    });

    this.elevationDiv = document.createElement('div');
    this.elevationDiv.className = 'map-elevation';
    this.elevationDiv.innerHTML = 'Elevation: 0 feet';

    this.map.addControl(
      this.elevationDiv,
      google.maps.ControlPosition.TOP_LEFT
    );

    this._subscriptions.push(
      this.map.click.subscribe((event) => {
        this.gaServices.trackEvent('Elevation Dialog', {
          action: 'Map Click',
        });

        this.updatePinLocation(event);
      })
    );
  }

  private async updatePinLocation(
    location: ILatLng,
    apiCall: boolean = true,
    recenter: boolean = false
  ): Promise<void> {
    if (apiCall) {
      const address = await this.mapsService.geocodeLatLng(location);
      const elevation = await this.mapsService.getElevation(location);

      if (!!address) this.addressSearch.setValue(address.formattedAddress);

      this.elevation = elevation;
    }

    const elevationText = `Elevation: ${formatNumber(
      this.elevation,
      'en-US',
      '1.0-0'
    )} feet`;

    this.elevationDiv!.innerHTML = elevationText;

    if (!this.mapPin) {
      this.mapPin = this.map.addMarker({
        id: 'pin',
        position: location,
        title: elevationText,
      });
    } else {
      this.mapPin.position = location;

      this.mapPin.title = elevationText;
    }

    if (recenter) {
      this.map.recenter(location, 16);
    }
  }
}
